import { useAuthContext } from 'components/context/AuthProvider'
import { useOptionalTeams } from 'features/Timeline/hooks/useTeams'
import { useCallback, useMemo } from 'react'
import { ShareMemberOption, ShareType } from '../models/shareForm'

/**
 * Recipient resolution shared by the article share form and the shared-note flow.
 * Mirrors the original logic in `ArticleShareForm`:
 *  - Entire Group  → all active team members except the current user
 *  - Specific Members → selected member `userId`s
 *
 * Safe outside TeamsProvider (admin/org layouts): returns empty recipients / null team.
 */
export function useShareRecipients() {
  const { user } = useAuthContext()
  const teams = useOptionalTeams()
  const teamMembersData = teams?.teamMembersData
  const isTeamMembersLoading = teams?.isTeamMembersLoading ?? false
  const activeTeamId = teams?.activeTeamId ?? null
  const activeTeamName = teams?.activeTeamName ?? null

  const teamMembersOptions = useMemo<ShareMemberOption[]>(() => {
    if (!teamMembersData) return []

    return teamMembersData
      .filter((member) => member.user?.id !== user?.id) // Exclude current user from the list
      .filter((member) => member.userId !== null)
      .map((member) => ({
        label: `${member.firstName} ${member.lastName} / (${member.email})`,
        value: (member.userId as number).toString(),
      }))
  }, [teamMembersData, user])

  const resolveUserIds = useCallback(
    (type: string, members: ShareMemberOption[]): number[] => {
      const source = type === ShareType.GROUP ? teamMembersOptions : members

      // The team members endpoint can return duplicate rows for the same user;
      // the share API rejects repeated recipients (`senderId must be unique`),
      // so de-duplicate the resolved ids.
      return Array.from(new Set(source.map((member) => parseInt(member.value))))
    },
    [teamMembersOptions],
  )

  return {
    teamMembersOptions,
    isTeamMembersLoading,
    activeTeamId,
    activeTeamName,
    resolveUserIds,
  }
}
